import {
  aliasDriverSpace, drivenRowsFor, parseDriverAddress,
  type AliasDriverFault, type AnswerRefAlias, type DriverAddress, type DriverRefusal,
  type DrivenRow,
} from '@agavi/blocks';
import { useMemo } from 'react';

import { useAnswerRefScope } from './answer-ref-context';

import type { Block } from '@/types/experience';

/**
 * WHAT A `drivenBy` PARTITIONS INTO — one derivation, for every surface that asks.
 *
 * Four files were each resolving the address, finding the block and calling `drivenRowsFor`
 * by hand: the projection, the issue list, the Varies-with row and the stimulus. That is the
 * shape this feature has already been bitten by twice — one fact with several readers, which
 * drift on the day the fact gains a case.
 *
 * Aliases ARE that day. An alias link partitions into the UNION of its members' options
 * (`aliasDriverSpace`), and four hand-rolled copies would have been four chances to merge
 * them differently — with the runtime selector, which merges in `@agavi/blocks`, as a fifth
 * opinion. A block whose rows disagreed with the rows its versions were authored against
 * shows a student the wrong question, which is the one failure the whole axis exists to
 * prevent.
 */
export interface DriverRows {
  address: DriverAddress | null;
  /** The answer space to author against, or null where there is none. */
  rows: DrivenRow[] | null;
  /** The driving block, for a block link. Absent for an alias, which has several. */
  driver?: Block;
  /** The alias, for an alias link, when the address resolves to one. */
  alias?: AnswerRefAlias;
  /**
   * Why an alias cannot drive, per offending member. Always empty for a block link, whose
   * single failure is simply `rows: null`.
   */
  faults: AliasDriverFault[];
  /**
   * WHOSE VARIABLE SCOPE A ROW'S HTML RESOLVES IN.
   *
   * A row's `html` is the DRIVER's authored option, so a chip in it must resolve against the
   * driver's task and not the follower's — a leader option reading `{site}` otherwise shows
   * the follower's value for that name, silently, which was a live bug.
   *
   * For an alias it is the current task, and legitimately: a member whose options carry a
   * TASK variable is refused outright, so any chip that survives into a union row is an
   * experience variable and resolves the same everywhere.
   */
  rowScopeTaskId?: string;
}

export function useDriverRows(drivenBy: string | undefined): DriverRows {
  const scope = useAnswerRefScope();
  /*
   * MEMOIZED ON THE SCOPE, which changes when the experience does and not when a sibling
   * re-renders.
   *
   * Every driven block mounts this twice — the card and the editor — and each of them
   * rebuilt the row set from scratch on every render. For an alias that is `aliasDriverSpace`
   * merging every member's options across every task, per keystroke, per instance. Reported
   * as "the system seems to become a bit sluggish ... in driven-by mode".
   *
   * The returned object is memoized too, not only the rows: `useDrivenVersionProjection`
   * reads a derived string key and does not care, but `use-driven-link-issues` and the link
   * menu both spread it into their own memos, where a fresh object every render is a fresh
   * dependency every render.
   */
  return useMemo(() => {
    const address = parseDriverAddress(drivenBy);
    if (!address) return { address: null, rows: null, faults: [] };

    if (address.kind === 'alias') {
      const alias = (scope?.aliases ?? []).find(a => a.id === address.aliasId);
      if (!alias) return { address, rows: null, faults: [] };
      const { rows, faults } = aliasDriverSpace(
        alias,
        (scope?.tasks ?? []) as Parameters<typeof aliasDriverSpace>[1],
        scope?.experienceVariableNames ?? new Set(),
      );
      return { address, rows, alias, faults, rowScopeTaskId: scope?.currentTaskId };
    }

    const driver = (scope?.tasks ?? []).find(tk => tk.id === address.taskId)
      ?.blocks?.find((b: Block) => b.id === address.blockId);
    /* The FACET names which answer space, for a block that has more than one — a FITB blank.
       Without it `drivenRowsFor` refuses a FITB outright, which is why it could not be a driver
       until the address could carry one. */
    const rows = driver
      ? drivenRowsFor(driver.type, (driver.data ?? {}) as Record<string, unknown>, address.facet)
      : null;
    return { address, rows, driver, faults: [], rowScopeTaskId: address.taskId };
  }, [drivenBy, scope]);
}

/**
 * WHY A BLOCK CANNOT DRIVE, in the teacher's words — one table, two readers.
 *
 * "Its answers cannot be listed" covered three different facts, and was wrong about the
 * commonest: a teacher whose MCQ has no options yet read it as a property of the TYPE and
 * went looking for another block, when the fix was to write the options. `driverRefusal`
 * tells the permanent cases from the ones that clear themselves; this says each in words.
 *
 * NO "Not available" PREFIX (owner). In the menu the row is greyed and `aria-disabled`, so
 * both the sight of it and the screen reader already say unavailable — a prefix spends the
 * first two words restating that instead of naming the cause.
 *
 * Shared with `useDrivenLinkIssues`, which says the same thing about an ALIAS member that
 * cannot drive. Two copies would be two wordings of one refusal, which is the drift this
 * feature has already produced twice.
 *
 * Spelled out rather than constructed: a template `blocks.scenario.refusal.${r}` is a key the
 * i18n check cannot verify.
 */
export const REFUSAL_REASON: Record<DriverRefusal, string> = {
  combination: 'blocks.scenario.refusal.combination',
  range: 'blocks.scenario.refusal.range',
  measurement: 'blocks.scenario.refusal.measurement',
  order: 'blocks.scenario.refusal.order',
  grouping: 'blocks.scenario.refusal.grouping',
  noOptions: 'blocks.scenario.refusal.noOptions',
  noPoints: 'blocks.scenario.refusal.noPoints',
  noWords: 'blocks.scenario.refusal.noWords',
  noBlankWords: 'blocks.scenario.refusal.noBlankWords',
};
